import { ModalContext } from 'app/app';
import axios from 'axios';
import { FieldInput } from 'components/fieldInput';
import { ViewContext, useNavigate } from 'components/lib';
import { Form, Formik } from 'formik';
import { Fragment, useContext, useEffect, useState } from 'react';
import InputUtils from 'helpers/inputUtils';
import * as Yup from 'yup';
import image from './images/loader.gif';
import styles from './styles.module.scss';
import { RunQuotePopup } from 'components/RunQuotePopup';

export function RunQuoteModal(props) {
  const context = useContext(ViewContext);
  const { modal, setModal } = useContext(ModalContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [isModalOpen, setModalOpen] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [runWithProgressive, setRunWithProgressive] = useState(false);
  const [quoteMode, setQuoteMode] = useState('existing');
  const [phone, setPhone] = useState('');

  const inputUtils = new InputUtils();

  const existingCompanyValidationSchema = Yup.object().shape({
    company: Yup.string().required('This field is required!'),
  });

  const newCompanyValidationSchema = Yup.object().shape({
    dot_number: Yup.string(),
    company_name: Yup.string().required('This field is required!'),
    dba_name: Yup.string(),
    owner_name: Yup.string().required('This field is required!'),
    phone: Yup.string()
      .required('This field is required!')
      .min(14, 'Number format invallid.'),
    email: Yup.string()
      .email('Invalid email!')
      .required('This field is required!'),
  });

  const handleContinue = () => {
    setModalOpen(false);
    if (formValues) {
      submitFormValues(formValues, runWithProgressive);
    }
  };

  const handleCancel = () => {
    setModalOpen(false);
  };

  async function runAPI() {
    await axios
      .get(`/api/company/list/all`)
      .then((res) => {
        setData(res.data.data);
      })
      .catch((err) => {
        context.notification.show(
          'Failed to load companies',
          'error',
          true,
          'toast',
          'x-circle'
        );
      });
  }

  useEffect(() => {
    if (modal.name === 'run_quote_modal') {
      setQuoteMode('existing');
      setPhone('');
      setFormValues(null);
      setModalOpen(false);
    }
  }, [modal.name]);

  useEffect(() => {
    if (modal.name === 'run_quote_modal') {
      runAPI();
    }
  }, [modal.name]);

  const handleChangePhoneNumber = (event, handleChange) => {
    handleChange(event);
    setPhone(inputUtils.number(event.target.value, phone));
  };

  const closeModal = () => {
    setModalOpen(false);
    setModal('');
  };

  const submitFormValues = async (values, runWithProgressive) => {
    setLoading(true);

    const apiEndpoint = runWithProgressive
      ? '/app/progressive/quote/run'
      : '/app/quote/run';

    try {
      if (quoteMode === 'new') {
        const res = await axios.post('/api/company', {
          dot_number: values.dot_number?.trim() || null,
          name: values.company_name.trim(),
          dba_name: values.dba_name?.trim() || '',
          owner_name: values.owner_name.trim(),
          phone: values.phone,
          email: values.email.trim().toLowerCase(),
          premium: 0,
          status: 'lead',
        });

        context.notification.show(
          'Company created successfully!',
          'success',
          true,
          'toast',
          'check-circle'
        );

        navigate(apiEndpoint, {
          state: {
            data: {
              company_id: res.data.data.id,
            },
          },
        });
      } else {
        const selectedCompany = data.find((o) => o.id === values.company);

        if (!selectedCompany) {
          throw new Error('Please select a company to continue.');
        }

        navigate(apiEndpoint, {
          state: {
            data: {
              company_id: selectedCompany.id,
            },
          },
        });
      }

      setModal({});
    } catch (err) {
      context.notification.show(
        err?.response?.data?.message || err.message || 'Failed to run quote',
        'error',
        true,
        'toast',
        'x-circle'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      {modal.name === 'run_quote_modal' && (
        <div className={styles.background}>
          <div className={styles.wrapper}>
            <div className={styles.insideWrapper}>
              <div className={styles.title}>Run Quote</div>
              <div className={styles.line} />
              <Formik
                enableReinitialize
                validationSchema={
                  quoteMode === 'new'
                    ? newCompanyValidationSchema
                    : existingCompanyValidationSchema
                }
                initialValues={{
                  company: data && data.length > 0 ? data[0].id : '',
                  dot_number: '',
                  company_name: '',
                  dba_name: '',
                  owner_name: '',
                  phone: '',
                  email: '',
                }}
                onSubmit={(values) => {
                  setFormValues(values);
                  setModalOpen(true);
                }}
              >
                {({
                  errors,
                  touched,
                  handleSubmit,
                  handleChange,
                  handleBlur,
                  submitForm,
                }) => (
                  <Fragment>
                    <Form
                      className={styles.form}
                      onSubmit={handleSubmit}
                    >
                      <div className={styles.modeSwitch}>
                        <button
                          type="button"
                          className={
                            quoteMode === 'existing'
                              ? styles.modeButtonActive
                              : styles.modeButton
                          }
                          onClick={() => {
                            setQuoteMode('existing');
                            setPhone('');
                          }}
                        >
                          Existing company
                        </button>
                        <button
                          type="button"
                          className={
                            quoteMode === 'new'
                              ? styles.modeButtonActive
                              : styles.modeButton
                          }
                          onClick={() => {
                            setQuoteMode('new');
                            setPhone('');
                          }}
                        >
                          New company
                        </button>
                      </div>
                      <div className={styles.modeHint}>
                        {quoteMode === 'existing'
                          ? 'Choose an existing company to start the quote with its saved profile.'
                          : 'Enter the minimum company details, create it, and continue straight into the quote.'}
                      </div>
                      <div className={styles.fieldRow}>
                        {quoteMode === 'existing' ? (
                          <FieldInput
                            id="company"
                            type="select"
                            label="Company"
                            placeholder="Company"
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            showErrors
                            errors={errors.company}
                            touched={touched.company}
                            required
                            options={data.map((value) => ({
                              value: value.id,
                              label: value.name,
                            }))}
                          />
                        ) : (
                          <>
                            <FieldInput
                              id="company_name"
                              type="text"
                              label="Company Name"
                              placeholder="Company Name"
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              showErrors
                              errors={errors.company_name}
                              touched={touched.company_name}
                              required
                            />
                            <FieldInput
                              id="dba_name"
                              type="text"
                              label="DBA Name"
                              placeholder="DBA Name"
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              showErrors
                              errors={errors.dba_name}
                              touched={touched.dba_name}
                            />
                          </>
                        )}
                      </div>
                      {quoteMode === 'new' && (
                        <>
                          <div className={styles.fieldRow}>
                            <FieldInput
                              id="owner_name"
                              type="text"
                              label="Owner Name"
                              placeholder="Owner Name"
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              showErrors
                              errors={errors.owner_name}
                              touched={touched.owner_name}
                              required
                            />
                            <FieldInput
                              id="phone"
                              type="phone"
                              label="Phone"
                              placeholder="Phone"
                              handleChange={(event) => {
                                handleChangePhoneNumber(event, handleChange);
                              }}
                              value={phone}
                              handleBlur={handleBlur}
                              showErrors
                              errors={errors.phone}
                              touched={touched.phone}
                              required
                            />
                          </div>
                          <div className={styles.fieldRow}>
                            <FieldInput
                              id="email"
                              type="text"
                              label="Email"
                              placeholder="Email"
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              showErrors
                              errors={errors.email}
                              touched={touched.email}
                              required
                            />
                            <FieldInput
                              id="dot_number"
                              type="text"
                              label="DOT Number"
                              placeholder="Optional"
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              showErrors
                              errors={errors.dot_number}
                              touched={touched.dot_number}
                            />
                          </div>
                        </>
                      )}
                      {quoteMode === 'existing' && data.length === 0 && (
                        <div className={styles.emptyState}>
                          No companies found yet. Switch to <strong>New company</strong> to create one and continue.
                        </div>
                      )}
                      {quoteMode === 'new' && (
                        <div className={styles.emptyState}>
                          The quote will open right after the company is created.
                        </div>
                      )}
                    </Form>
                    <div className={styles.footer}>
                      <div className={styles.buttons}>
                        <button
                          className={styles.cancel}
                          onClick={closeModal}
                        >
                          Cancel
                        </button>
                        <button
                          className={styles.save}
                          style={
                            !loading
                              ? { cursor: 'pointer' }
                              : { cursor: 'default' }
                          }
                          onClick={() => {
                            setRunWithProgressive(false);
                            !loading && submitForm();
                          }}
                        >
                          {loading ? (
                            <img
                              src={image}
                              alt="loading"
                              style={{ width: '25px' }}
                            />
                          ) : quoteMode === 'new' ? (
                            'Create & Run'
                          ) : (
                            'Run'
                          )}
                        </button>
                      </div>
                    </div>
                  </Fragment>
                )}
              </Formik>
            </div>
          </div>
        </div>
      )}
      {isModalOpen && (
        <RunQuotePopup
          onContinue={handleContinue}
          onCancel={handleCancel}
        />
      )}
    </Fragment>
  );
}
